
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import {Mail, MessageSquare, Send} from 'lucide-react'
import { Link } from 'react-router-dom'
import { lumi } from '../lib/lumi'

interface ContactForm {
  fullName: string
  email: string
  country: string
  message: string
}

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { register, handleSubmit, reset, formState: { errors } } = useForm<ContactForm>()

  const onSubmit = async (data: ContactForm) => {
    setIsSubmitting(true)
    try {
      // Save to Lumi database
      await lumi.entities.Contacts.create({
        fullName: data.fullName,
        email: data.email,
        country: data.country,
        message: data.message,
        handled: false,
        createdAt: new Date().toISOString()
      })

      toast.success('Message sent successfully! We\'ll get back to you soon.')
      reset()
    } catch (error) {
      console.error('Error submitting contact form:', error)
      toast.error('Failed to send message. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen py-20 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-primary-navy mb-6">
            Questions? We're here to help.
          </h1>
          <p className="text-xl text-primary-slate">
            Get in touch with our team for personalized guidance on your global nursing career. We usually respond within 24 hours.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-8 border border-gray-200">
            <h2 className="text-2xl font-bold text-primary-navy mb-6">Send us a message</h2>
            
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-primary-navy mb-2">
                  Full Name *
                </label>
                <input
                  {...register('fullName', { required: 'Full name is required' })}
                  type="text"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-teal focus:border-transparent"
                  placeholder="Your full name"
                />
                {errors.fullName && (
                  <p className="mt-1 text-sm text-red-600">{errors.fullName.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-primary-navy mb-2">
                  Email Address *
                </label>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  type="email"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-teal focus:border-transparent"
                  placeholder="your.email@example.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="country" className="block text-sm font-medium text-primary-navy mb-2">
                  Country *
                </label>
                <select
                  {...register('country', { required: 'Country is required' })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-teal focus:border-transparent"
                >
                  <option value="">Select your country</option>
                  <option value="Pakistan">Pakistan</option>
                  <option value="India">India</option>
                  <option value="Philippines">Philippines</option>
                  <option value="Kuwait">Kuwait</option>
                  <option value="Saudi Arabia">Saudi Arabia</option>
                  <option value="UAE">UAE</option>
                  <option value="Qatar">Qatar</option>
                  <option value="Bahrain">Bahrain</option>
                  <option value="Oman">Oman</option>
                  <option value="Other">Other</option>
                </select>
                <p className="mt-1 text-sm text-primary-slate">We'll tailor our response based on your country.</p>
                {errors.country && (
                  <p className="mt-1 text-sm text-red-600">{errors.country.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-primary-navy mb-2">
                  Message *
                </label>
                <textarea
                  {...register('message', { required: 'Message is required' })}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-teal focus:border-transparent"
                  placeholder="Tell us how we can help you (e.g., NCLEX, CGFNS/VisaScreen, Dataflow, UK/GCC/US route)..."
                />
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary-teal text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-navy transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Message
                  </>
                )}
              </button>
            </form>

            <div className="text-center mt-4">
              <p className="text-primary-slate">
                Looking for instant answers? Visit our <Link to="/resources" className="text-primary-teal hover:text-primary-navy font-medium">Resources</Link> page or check our <a href="/#faq" className="text-primary-teal hover:text-primary-navy font-medium">FAQ section</a>.
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-primary-light rounded-2xl p-8">
              <h3 className="text-xl font-bold text-primary-navy mb-6">Other ways to reach us</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <Mail className="h-6 w-6 text-primary-teal mt-1" />
                  <div>
                    <h4 className="font-semibold text-primary-navy">Email</h4>
                    <p className="text-primary-slate">hello@nursinghive.com</p>
                    <p className="text-sm text-primary-slate mt-1">We typically respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <MessageSquare className="h-6 w-6 text-primary-teal mt-1" />
                  <div>
                    <h4 className="font-semibold text-primary-navy">WhatsApp</h4>
                    <p className="text-primary-slate">Join our support group</p>
                    <a 
                      href="https://chat.whatsapp.com/G9KobSl1sV08fPw5m3GIwy?mode=wwt"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-teal hover:text-primary-navy transition-colors text-sm mt-1 inline-block"
                    >
                      Join WhatsApp Group →
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-primary-navy mb-4">Quick Questions?</h3>
              <p className="text-primary-slate mb-4">
                Check our resources page for immediate answers to common questions about NCLEX, CGFNS, and international nursing careers.
              </p>
              <Link 
                to="/resources"
                className="text-primary-teal hover:text-primary-navy transition-colors font-medium"
              >
                Browse Resources →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Contact
